import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { BellRing, X } from 'lucide-react'
import { isPushSupported, subscribeToPushNotifications } from '@/lib/push'

const PERMISSION_DISMISS_KEY = 'xafra-bi-push-banner-dismissed'

/**
 * One-time banner asking for web push permission.
 * Rendered via React portal into document.body so it is never clipped or
 * mispositioned by the sidebar's overflow-hidden / backdrop-filter ancestors.
 */
export default function PushPermissionBanner() {
  const [visible, setVisible] = useState(false)
  const [subscribing, setSubscribing] = useState(false)

  useEffect(() => {
    const dismissed = localStorage.getItem(PERMISSION_DISMISS_KEY)
    if (
      isPushSupported() &&
      !dismissed &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      setVisible(true)
    }
  }, [])

  const handleActivatePush = async () => {
    setSubscribing(true)
    const ok = await subscribeToPushNotifications()
    setSubscribing(false)
    if (ok) {
      setVisible(false)
      localStorage.setItem(PERMISSION_DISMISS_KEY, '1')
    }
  }

  const handleDismiss = () => {
    setVisible(false)
    localStorage.setItem(PERMISSION_DISMISS_KEY, '1')
  }

  if (!visible) return null

  return createPortal(
    <div className="fixed z-[100] bottom-20 md:bottom-6 right-4 left-4 md:left-auto md:right-6 md:w-[380px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-orange-50 dark:bg-orange-900/30 rounded-lg shrink-0">
          <BellRing className="w-5 h-5 text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ¿Quieres recibir notificaciones en tu móvil?
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Te avisaremos cuando las estadísticas se actualicen o fallen.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleActivatePush}
              disabled={subscribing}
              className="px-3 py-1.5 text-sm rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-60 transition-colors"
            >
              {subscribing ? 'Activando...' : 'Activar'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
            >
              Ahora no
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Cerrar"
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>,
    document.body
  )
}
